import { type BasicObject } from '@pkg/types';
import { mergeDeduplicatedArrays } from '@pkg/utils';

import { type FilterSingleValue } from './types';
export class Filter<K = string, V = FilterSingleValue> extends Map<K, V[]> {
    get parse() {
        const converted: BasicObject = Object.fromEntries(this);
        return converted;
    }

    get serialize() {
        return JSON.stringify(this.parse);
    }

    get urlSearchParams() {
        const entries = [...this.entries()];
        const record = entries.reduce<Record<string, string>>((list, [key, value]) => {
            const stringKey = `${key}`;
            return {
                ...list,
                [stringKey]: value.join(','),
            };
        }, {});

        return new URLSearchParams(record);
    }

    get total() {
        const values = [...this.values()];
        return values.reduce((total, value) => total + value.length, 0);
    }

    append(key: K, value: V, ...moreValues: V[]) {
        const currentValues = this.get(key);
        const newValues = mergeDeduplicatedArrays(currentValues ?? [], [value], moreValues);

        return this.set(key, newValues);
    }

    remove(key: K, value: V, ...moreValues: V[]) {
        const currentValues = this.get(key);

        if (!currentValues?.length) return;

        const valuesToRemove = mergeDeduplicatedArrays([value], moreValues);
        const retainedValues = currentValues.filter((item) => !valuesToRemove.includes(item));

        return retainedValues.length ? this.set(key, retainedValues) : this.delete(key);
    }

    getLastValue(key: K) {
        const currentValues = this.get(key);
        return currentValues ? currentValues[currentValues.length - 1] : undefined;
    }

    hasSomeValue(key: K, value: V, ...moreValues: V[]) {
        const currentValues = this.get(key);

        if (!currentValues?.length) return false;

        const valuesToCompare = mergeDeduplicatedArrays([value], moreValues);
        return currentValues.some((item) => valuesToCompare.includes(item));
    }
}
